import { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { GOLD, EMBER, MUTE, FAINT, GHOST, MONO, BODY } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Ic } from './shared';
import * as api from '../lib/api';

const DEBT_TYPES = [
  { id: 'credit', label: 'Credit Card', icon: 'credit', examples: 'Visa, Mastercard, Amex, Discover' },
  { id: 'loan', label: 'Personal Loan', icon: 'loan', examples: 'SoFi, LendingClub, Marcus' },
  { id: 'auto', label: 'Auto Loan', icon: 'car', examples: 'Car payment, motorcycle' },
  { id: 'bnpl', label: 'Buy Now Pay Later', icon: 'credit', examples: 'Affirm, Klarna, Afterpay' },
  { id: 'student', label: 'Student Loan', icon: 'loan', examples: 'Federal, private, Sallie Mae' },
  { id: 'medical', label: 'Medical Debt', icon: 'loan', examples: 'Hospital bills, dental' },
  { id: 'other', label: 'Other', icon: 'loan', examples: 'Collections, family loan, etc.' },
];

function DebtCard({ debt, index, onRemove, onEdit }) {
  const typeInfo = DEBT_TYPES.find(t => t.id === debt.type) || DEBT_TYPES[0];
  return (
    <div data-testid={`debt-card-${index}`} className="dettio-debt-card">
      <div className="dettio-debt-card__header">
        <div className="dettio-debt-card__num">
          <Mo style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>#{index + 1}</Mo>
        </div>
        <div className="dettio-flex-1">
          <Bd style={{ fontSize: 15, fontWeight: 600, color: 'var(--light)' }}>{debt.name}</Bd>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
            {typeInfo.label} · {debt.apr}% APR
          </Mo>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Mo style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>${Number(debt.balance).toLocaleString()}</Mo>
          <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
            ${Number(debt.min_payment).toLocaleString()}/mo
          </Mo>
        </div>
      </div>
      <div className="dettio-debt-card__actions">
        <button onClick={() => onEdit(index)} data-testid={`edit-debt-${index}`} className="dettio-btn-text dettio-flex-1" style={{ padding: 8, border: `1px solid ${GHOST}` }}>EDIT</button>
        <button onClick={() => onRemove(index)} data-testid={`remove-debt-${index}`} className="dettio-btn-cancel dettio-flex-1">REMOVE</button>
      </div>
    </div>
  );
}

function DebtForm({ onSubmit, onCancel, initial, isFirst }) {
  const [type, setType] = useState(initial?.type || '');
  const [name, setName] = useState(initial?.name || '');
  const [balance, setBalance] = useState(initial?.balance || '');
  const [original, setOriginal] = useState(initial?.original || '');
  const [apr, setApr] = useState(initial?.apr || '');
  const [minPayment, setMinPayment] = useState(initial?.min_payment || '');
  const [due, setDue] = useState(initial?.due || '');
  const [step, setStep] = useState(initial ? 1 : 0);

  if (step === 0) {
    return (
      <div data-testid="debt-type-picker" className="fade-in">
        <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 14 }}>
          {isFirst ? 'WHAT KIND OF ACCOUNT IS IT?' : 'ADD ANOTHER'}
        </Mo>
        <div className="dettio-type-grid">
          {DEBT_TYPES.map(dt => (
            <button key={dt.id} onClick={() => { setType(dt.id); setStep(1); }}
              data-testid={`type-${dt.id}`}
              className={`dettio-type-btn${type === dt.id ? ' dettio-type-btn--active' : ''}`}>
              <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)', display: 'block' }}>{dt.label}</Bd>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginTop: 4, lineHeight: 1.4 }}>{dt.examples}</Mo>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const selectedType = DEBT_TYPES.find(t => t.id === type);
  const isBnpl = type === 'bnpl';

  const handleSubmit = () => {
    if (!name || !balance || !original) return;
    onSubmit({
      name, type,
      balance: parseFloat(balance),
      original: parseFloat(original),
      apr: parseFloat(apr) || 0,
      min_payment: parseFloat(minPayment) || 0,
      due: due || '',
    });
  };

  return (
    <div data-testid="debt-form" className="fade-in">
      <button onClick={() => setStep(0)} className="dettio-btn-text dettio-btn-text--ghost" style={{ marginBottom: 12, padding: 0, width: 'auto' }}>
        ← CHANGE TYPE
      </button>

      <div className="dettio-badge dettio-badge--gold" style={{ marginBottom: 16 }}>
        {selectedType?.label.toUpperCase()}
      </div>

      <div style={{ marginBottom: 4 }}>
        <Mo className="dettio-form-label">
          {isBnpl ? 'SERVICE NAME' : 'ACCOUNT NAME'}
        </Mo>
        <input
          data-testid="debt-name-input"
          placeholder={isBnpl ? 'e.g. Affirm - MacBook' : type === 'credit' ? 'e.g. Chase Sapphire' : 'e.g. SoFi Personal'}
          value={name} onChange={e => setName(e.target.value)} className="dettio-input"
        />
      </div>

      <div className="dettio-flex dettio-flex--gap-sm" style={{ marginBottom: 4 }}>
        <div className="dettio-flex-1">
          <Mo className="dettio-form-label">CURRENT BALANCE</Mo>
          <input
            data-testid="debt-balance-input"
            type="number" placeholder="$0.00"
            value={balance} onChange={e => setBalance(e.target.value)} className="dettio-input"
          />
        </div>
        <div className="dettio-flex-1">
          <Mo className="dettio-form-label">
            {isBnpl ? 'TOTAL ORDER' : 'ORIGINAL AMOUNT'}
          </Mo>
          <input
            data-testid="debt-original-input"
            type="number" placeholder="$0.00"
            value={original} onChange={e => setOriginal(e.target.value)} className="dettio-input"
          />
        </div>
      </div>

      <div className="dettio-flex dettio-flex--gap-sm" style={{ marginBottom: 4 }}>
        <div className="dettio-flex-1">
          <Mo className="dettio-form-label">
            APR %{isBnpl && <span style={{ color: 'var(--ghost)' }}> (0 if interest-free)</span>}
          </Mo>
          <input
            data-testid="debt-apr-input"
            type="number" placeholder="0.00"
            value={apr} onChange={e => setApr(e.target.value)} className="dettio-input"
          />
        </div>
        <div className="dettio-flex-1">
          <Mo className="dettio-form-label">MIN. PAYMENT</Mo>
          <input
            data-testid="debt-min-input"
            type="number" placeholder="$0.00"
            value={minPayment} onChange={e => setMinPayment(e.target.value)} className="dettio-input"
          />
        </div>
      </div>

      <Mo className="dettio-form-label">
        DUE DATE <span style={{ color: 'var(--ghost)' }}>(optional)</span>
      </Mo>
      <input
        data-testid="debt-due-input"
        placeholder="e.g. 15th of each month"
        value={due} onChange={e => setDue(e.target.value)} className="dettio-input"
      />

      <div style={{ marginTop: 16 }}>
        <Btn onClick={handleSubmit} gold
          disabled={!name || !balance || !original}
          data-testid="save-debt-btn">
          {initial ? 'UPDATE' : 'ADD THIS ACCOUNT'}
        </Btn>
        {onCancel && (
          <button onClick={onCancel} data-testid="cancel-debt-btn" className="dettio-btn-text" style={{ marginTop: 8 }}>
            CANCEL
          </button>
        )}
      </div>
    </div>
  );
}

export function DebtOnboarding({ onComplete }) {
  const [phase, setPhase] = useState('intro');
  const [debts, setDebts] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [linkToken, setLinkToken] = useState(null);

  const totalDebt = debts.reduce((s, d) => s + d.balance, 0);
  const totalMin = debts.reduce((s, d) => s + d.min_payment, 0);

  // Fetch Plaid link token on mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/create_link_token`, {
          method: 'POST',
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setLinkToken(data.link_token);
        }
      } catch (e) { console.error(e); }
    };
    fetchLinkToken();
  }, []);

  // Plaid Link success handler
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setSyncing(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/exchange_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: publicToken, metadata }),
        credentials: 'include'
      });
      // Bank connected, proceed to done
      setSyncing(false);
      setPhase('done');
    } catch (e) { 
      console.error(e);
      setSyncing(false);
    }
  }, []);

  // Initialize Plaid Link
  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {},
  });

  const handleAddDebt = (debt) => {
    if (editingIndex !== null) {
      const updated = [...debts];
      updated[editingIndex] = debt;
      setDebts(updated);
      setEditingIndex(null);
    } else {
      setDebts([...debts, debt]);
    }
    setPhase('list');
  };

  const handleRemove = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setPhase('form');
  };

  const handleFinish = async () => {
    setSaving(true);
    for (const debt of debts) {
      try {
        await api.createDebt({
          name: debt.name,
          type: debt.type,
          balance: debt.balance,
          original: debt.original,
          apr: debt.apr,
          min_payment: debt.min_payment,
          extra: 0,
          due: debt.due,
          is_target: false,
        });
      } catch (e) { console.error('Failed to save debt:', e); }
    }
    // Mark first debt as target (smallest balance for snowball default)
    setSaving(false);
    setPhase('done');
  };

  // Intro screen with bank connect option
  if (phase === 'intro') return (
    <div data-testid="debt-onboarding-intro" className="dettio-onboarding--intro" style={{ position: 'relative' }}>
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner dettio-screen__center">
        <div style={{ marginBottom: 20 }}>{Ic.flame(48, GOLD)}</div>
        <Dp style={{ fontSize: 26, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1, marginBottom: 10 }}>
          Time to face the numbers
        </Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.8, display: 'block', marginBottom: 8 }}>
          No judgment here. Just clarity.
        </Bd>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.8, display: 'block', marginBottom: 24 }}>
          Connect your bank to auto-import your debts, or add them manually.
        </Bd>

        {/* Bank Connect Option */}
        <div data-testid="onboarding-bank-connect" className="dettio-bank-intro">
          <div className="dettio-bank-intro__header">
            {Ic.bank(24, GOLD)}
            <div>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>Connect Your Bank</Bd>
              <Bd style={{ fontSize: 11, color: 'var(--mute)' }}>Auto-import credit cards, loans, Affirm, Klarna & more</Bd>
            </div>
          </div>
          <Btn onClick={() => plaidReady && openPlaidLink()} gold disabled={syncing || !plaidReady} data-testid="onboarding-bank-btn">
            {syncing ? 'CONNECTING...' : !plaidReady ? 'LOADING...' : 'CONNECT YOUR BANK'}
          </Btn>
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', textAlign: 'center', marginTop: 10 }}>
            Powered by Plaid · 11,000+ banks · Bank-grade encryption
          </Mo>
        </div>

        <div className="dettio-divider">
          <div className="dettio-divider__line" />
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', letterSpacing: 1 }}>OR</Mo>
          <div className="dettio-divider__line" />
        </div>

        {/* Manual Entry Option */}
        <div className="dettio-info-box dettio-info-box--surface" style={{ marginBottom: 24, textAlign: 'left' }}>
          {[
            { n: '1', t: 'Add each account', d: 'Credit cards, loans, BNPL — everything' },
            { n: '2', t: 'We build your plan', d: 'Math-optimized payoff strategy' },
            { n: '3', t: 'Watch them fall', d: 'One by one until you\'re free' },
          ].map((s, i) => (
            <div key={i} className={`dettio-strategy-step${i === 2 ? ' dettio-strategy-step--last' : ''}`}>
              <div className="dettio-strategy-step__num">
                <Mo style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>{s.n}</Mo>
              </div>
              <div>
                <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>{s.t}</Bd>
                <Bd style={{ fontSize: 11, color: 'var(--mute)', marginTop: 2 }}>{s.d}</Bd>
              </div>
            </div>
          ))}
        </div>

        <Btn onClick={() => setPhase('form')} secondary data-testid="start-adding-btn">
          ADD MANUALLY INSTEAD
        </Btn>
      </div>
    </div>
  );

  // Form screen
  if (phase === 'form') return (
    <div data-testid="debt-onboarding-form" className="dettio-onboarding" style={{ position: 'relative' }}>
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        {debts.length > 0 && (
          <div className="dettio-badge dettio-badge--gold" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 12px' }}>
            <Mo style={{ fontSize: 10, fontWeight: 700 }}>{debts.length}</Mo>
            <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>
              {debts.length === 1 ? 'account' : 'accounts'} added · ${totalDebt.toLocaleString()} total
            </Mo>
          </div>
        )}
        <DebtForm
          onSubmit={handleAddDebt}
          onCancel={debts.length > 0 ? () => { setEditingIndex(null); setPhase('list'); } : null}
          initial={editingIndex !== null ? debts[editingIndex] : null}
          isFirst={debts.length === 0 && editingIndex === null}
        />
      </div>
    </div>
  );

  // Done screen
  if (phase === 'done') return (
    <div data-testid="debt-onboarding-done" className="dettio-onboarding--done" style={{ position: 'relative' }}>
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner dettio-screen__center">
        <div style={{ marginBottom: 20 }}>{Ic.check(48, GOLD)}</div>
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 8 }}>
          {debts.length > 0 ? `${debts.length} ${debts.length === 1 ? 'account' : 'accounts'} locked in` : 'Bank connected!'}
        </Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 6 }}>
          {debts.length > 0 ? (
            <>Total: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>${totalDebt.toLocaleString()}</span></>
          ) : (
            'Your accounts are syncing automatically'
          )}
        </Bd>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 32 }}>
          Now let's build a strategy to destroy every single one.
        </Bd>
        <Btn onClick={onComplete} gold data-testid="continue-to-strategy-btn">
          BUILD MY STRATEGY
        </Btn>
      </div>
    </div>
  );

  // List screen (shows added debts + option to add more)
  return (
    <div data-testid="debt-onboarding-list" className="dettio-onboarding" style={{ position: 'relative' }}>
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>
            THE FULL PICTURE
          </Mo>
          <Dp style={{ fontSize: 34, fontWeight: 800, color: 'var(--light)', letterSpacing: -2 }}>
            ${totalDebt.toLocaleString()}
          </Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 4 }}>
            across {debts.length} {debts.length === 1 ? 'account' : 'accounts'} · ${totalMin.toLocaleString()}/mo minimum
          </Mo>
        </div>

        {debts.map((d, i) => (
          <DebtCard key={i} debt={d} index={i} onRemove={handleRemove} onEdit={handleEdit} />
        ))}

        <button onClick={() => { setEditingIndex(null); setPhase('form'); }}
          data-testid="add-another-debt-btn"
          className="dettio-add-btn" style={{ marginTop: 8, marginBottom: 20 }}>
          + ADD ANOTHER ACCOUNT
        </button>

        <Btn onClick={handleFinish} gold disabled={saving} data-testid="done-adding-btn">
          {saving ? 'SAVING...' : debts.length === 1 ? "THAT'S MY ONLY ONE" : "THAT'S ALL OF THEM"}
        </Btn>

        <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', textAlign: 'center', marginTop: 12, lineHeight: 1.6 }}>
          You can always add more later from the Plan tab
        </Mo>
      </div>
    </div>
  );
}
